import type { ApiClient } from '../api/client';
import type { HookStatus, Pipeline, PipelineJob } from '../types';

import { createLiveResource, type LiveResource } from './liveResource';

export type AppGitlabRuntimeData = {
  pipelines: Pipeline[];
  hookStatus: HookStatus | null;
  pipelineJobs: Record<number, PipelineJob[]>;
};

export type AppGitlabRuntimeResource = {
  live: LiveResource<AppGitlabRuntimeData>;
  setPipelineLimit: (limit: number) => void;
  getPipelineLimit: () => number;
  watchPipelineJobs: (pipelineId: number, watch: boolean) => Promise<void>;
  clearWatchedPipelineJobs: () => void;
  refresh: (reason?: string) => Promise<void>;
};

const resourcesByClient = new WeakMap<ApiClient, Map<string, AppGitlabRuntimeResource>>();

function normalizeFetchReasonSource(reason: string): string {
  if (reason === 'poll') {
    return 'poller';
  }
  if (reason === 'subscribe' || reason === 'bootstrap' || reason.startsWith('manual')) {
    return 'direct';
  }
  if (
    reason === 'watch-pipeline-jobs' ||
    reason === 'unwatch-pipeline-jobs' ||
    reason === 'pipeline-jobs-bootstrap'
  ) {
    return 'direct';
  }
  return reason;
}

function hookEventAdvanced(
  previousHookStatus: HookStatus | null,
  nextHookStatus: HookStatus | null
): boolean {
  if (!previousHookStatus || !nextHookStatus) {
    return false;
  }
  return (
    previousHookStatus.last_event_at !== nextHookStatus.last_event_at ||
    previousHookStatus.last_event_job_id !== nextHookStatus.last_event_job_id ||
    previousHookStatus.last_event_pipeline_id !== nextHookStatus.last_event_pipeline_id ||
    previousHookStatus.last_event_status !== nextHookStatus.last_event_status
  );
}

function deployTriggerAdvanced(
  previousHookStatus: HookStatus | null,
  nextHookStatus: HookStatus | null
): boolean {
  if (!previousHookStatus || !nextHookStatus) {
    return false;
  }
  return (
    previousHookStatus.last_deploy_trigger_at !== nextHookStatus.last_deploy_trigger_at ||
    previousHookStatus.last_deploy_trigger_job_id !== nextHookStatus.last_deploy_trigger_job_id ||
    previousHookStatus.last_deploy_trigger_pipeline_id !==
      nextHookStatus.last_deploy_trigger_pipeline_id
  );
}

export function resolveAppGitlabRuntimeSource(
  nextData: AppGitlabRuntimeData,
  previousData: AppGitlabRuntimeData | null,
  reason: string
): string {
  if (!previousData) {
    return normalizeFetchReasonSource(reason);
  }
  const previousHookStatus = previousData.hookStatus;
  const nextHookStatus = nextData.hookStatus;

  if (deployTriggerAdvanced(previousHookStatus, nextHookStatus)) {
    const triggerSource = nextHookStatus?.last_deploy_trigger_source
      ?.trim()
      .toLowerCase();
    if (triggerSource) {
      return triggerSource;
    }
  }

  if (hookEventAdvanced(previousHookStatus, nextHookStatus)) {
    return 'hook';
  }

  return normalizeFetchReasonSource(reason);
}

function isPipelineFinished(status: string): boolean {
  return ['success', 'failed', 'canceled', 'skipped'].includes(status.trim().toLowerCase());
}

function isManualOrPendingJob(job: PipelineJob): boolean {
  const normalized = job.status.trim().toLowerCase();
  return (
    normalized === 'manual' ||
    normalized === 'created' ||
    normalized === 'pending' ||
    normalized === 'scheduled' ||
    normalized === 'waiting_for_resource'
  );
}

function resolvePollInterval(
  data: AppGitlabRuntimeData | null,
  watchedPipelineIds: Set<number>
): number | null {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    return null;
  }
  if (!data) {
    return 4000;
  }

  const hasDeployWait = data.pipelines.some(
    (pipeline) =>
      pipeline.deploy_wait_active === true ||
      (pipeline.deploy_waits && pipeline.deploy_waits.length > 0)
  );

  const hasActivePipeline = data.pipelines.some(
    (pipeline) => !isPipelineFinished(pipeline.status)
  );

  const hasWatchedPendingJobs = [...watchedPipelineIds].some((pipelineId) => {
    const jobs = data.pipelineJobs[pipelineId] ?? [];
    return jobs.some(isManualOrPendingJob);
  });

  const hookInstalled = data.hookStatus?.installed === true;

  if (hasDeployWait || hasWatchedPendingJobs) {
    return hookInstalled ? 4000 : 2500;
  }

  if (hasActivePipeline) {
    return hookInstalled ? 10000 : 5000;
  }

  if (watchedPipelineIds.size > 0) {
    return hookInstalled ? 15000 : 7000;
  }

  return null;
}

function createAppGitlabRuntimeResource(
  apiClient: ApiClient,
  appId: string
): AppGitlabRuntimeResource {
  let pipelineLimit = 5;
  const watchedPipelineIds = new Set<number>();
  let pipelineJobsCache: Record<number, PipelineJob[]> = {};

  const live = createLiveResource<AppGitlabRuntimeData>({
    id: `app-gitlab-runtime:${appId}`,
    fetch: async () => {
      if (!appId) {
        return {
          pipelines: [],
          hookStatus: null,
          pipelineJobs: {}
        };
      }
      const [pipelines, hookStatus] = await Promise.all([
        apiClient.get<Pipeline[]>(`/api/apps/${appId}/pipelines?limit=${pipelineLimit}`),
        apiClient.get<HookStatus>(`/api/apps/${appId}/gitlab/hook`)
      ]);

      const visiblePipelineIds = new Set(pipelines.map((pipeline) => pipeline.id));
      for (const watchedId of [...watchedPipelineIds]) {
        if (!visiblePipelineIds.has(watchedId)) {
          watchedPipelineIds.delete(watchedId);
        }
      }

      if (watchedPipelineIds.size > 0) {
        const fetches = [...watchedPipelineIds].map(async (pipelineId) => {
          const jobs = await apiClient.get<PipelineJob[]>(
            `/api/apps/${appId}/pipelines/${pipelineId}/jobs`
          );
          return [pipelineId, jobs] as const;
        });
        const results = await Promise.all(fetches);
        const nextCache: Record<number, PipelineJob[]> = {};
        for (const [pipelineId, jobs] of results) {
          nextCache[pipelineId] = jobs;
        }
        pipelineJobsCache = nextCache;
      } else {
        pipelineJobsCache = {};
      }

      return {
        pipelines,
        hookStatus,
        pipelineJobs: { ...pipelineJobsCache }
      };
    },
    resolveSource: resolveAppGitlabRuntimeSource,
    pollIntervalMs: (snapshot) => resolvePollInterval(snapshot.data, watchedPipelineIds),
    isEqual: (a, b) => JSON.stringify(a) === JSON.stringify(b)
  });

  const setPipelineLimit = (limit: number) => {
    pipelineLimit = Math.max(1, Math.floor(limit));
  };

  const watchPipelineJobs = async (pipelineId: number, watch: boolean) => {
    if (!appId) {
      return;
    }
    if (watch) {
      watchedPipelineIds.add(pipelineId);
    } else {
      watchedPipelineIds.delete(pipelineId);
      const nextCache = { ...pipelineJobsCache };
      delete nextCache[pipelineId];
      pipelineJobsCache = nextCache;
    }
    await live.refresh(watch ? 'watch-pipeline-jobs' : 'unwatch-pipeline-jobs');
  };

  const clearWatchedPipelineJobs = () => {
    if (watchedPipelineIds.size === 0 && Object.keys(pipelineJobsCache).length === 0) {
      return;
    }
    watchedPipelineIds.clear();
    pipelineJobsCache = {};
    live.updateLocal(
      (current) =>
        current
          ? {
              ...current,
              pipelineJobs: {}
            }
          : current,
      'clear-watched-pipeline-jobs'
    );
  };

  const refresh = async (reason = 'manual') => {
    if (!appId) {
      return;
    }
    await live.refresh(reason);
  };

  return {
    live,
    setPipelineLimit,
    getPipelineLimit: () => pipelineLimit,
    watchPipelineJobs,
    clearWatchedPipelineJobs,
    refresh
  };
}

export function getAppGitlabRuntimeResource(
  apiClient: ApiClient,
  appId: string
): AppGitlabRuntimeResource {
  let byApp = resourcesByClient.get(apiClient);
  if (!byApp) {
    byApp = new Map<string, AppGitlabRuntimeResource>();
    resourcesByClient.set(apiClient, byApp);
  }
  const existing = byApp.get(appId);
  if (existing) {
    return existing;
  }

  const created = createAppGitlabRuntimeResource(apiClient, appId);
  byApp.set(appId, created);
  return created;
}
